import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../components/AuthContext';
import { canSwitchBranch as canSwitchBranchPermission } from '../../lib/permissions';
import { IconPrinter, IconSearch, IconX, IconChevronLeft, IconPackage } from '@tabler/icons-react';
import BatchPrintModal from '../../components/inventory/BatchPrintModal';

const BRANCHES = [
  { id: 1, name: 'Dallas' },
  { id: 2, name: 'Houston' },
  { id: 3, name: 'Austin' },
  { id: 4, name: 'San Antonio' },
];

function getStockForBranch(item, branchId) {
  if (!Array.isArray(item.inventory_stock)) return null;
  return item.inventory_stock.find(s => s.branch_id === branchId) || null;
}

export default function InventoryLabelPrint({ setActive }) {
  const { profile, isAdmin, isManager, isExecutive, labelPrintingEnabled, companyName } = useAuth();
  const isMobile = window.innerWidth <= 768;
  const canSwitchBranch = canSwitchBranchPermission({ isAdmin, isManager, isExecutive });

  const [branchId, setBranchId] = useState(null);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [selected, setSelected] = useState({});
  // Session 76. One modal replaces both the hardcoded Brother button and the
  // separate Canon export panel — size, quantity and destination are one
  // decision made in one place, which is the gate's callout B.
  const [showPrintModal, setShowPrintModal] = useState(false);

  useEffect(() => {
    if (profile?.branch_id) setBranchId(profile.branch_id);
  }, [profile]);

  const branchName = BRANCHES.find(b => b.id === branchId)?.name || '';

  const load = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    const { data } = await supabase
      .from('inventory_items')
      .select(`
        id, name, sku, uom, is_active,
        inventory_categories(id, name, parent_id),
        inventory_stock(id, qty_on_hand, branch_id)
      `)
      .eq('is_active', true);

    const branchFiltered = (data || []).filter(item =>
      item.inventory_stock?.some(s => s.branch_id === branchId)
    );
    setItems(branchFiltered);

    const { data: cats } = await supabase
      .from('inventory_categories')
      .select('id, name, parent_id')
      .is('parent_id', null)
      .order('name');
    setCategories(cats || []);
    setLoading(false);
  }, [branchId]);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter(item => {
    if (filterCategory && item.inventory_categories?.id !== parseInt(filterCategory) && item.inventory_categories?.parent_id !== parseInt(filterCategory)) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = [item.name, item.sku, item.inventory_categories?.name].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const selectedIds = Object.keys(selected).filter(id => selected[id]?.checked);
  const totalLabels = selectedIds.reduce((sum, id) => sum + (selected[id]?.copies || 1), 0);
  const selectedItems = selectedIds.map(id => selected[id].item);
  // The per-row copies boxes on this page seed the modal. This is the capability
  // the swap had to preserve: the gate listed per-item copies as out of scope
  // (D10) on the understanding it did not exist yet — on this screen it did.
  const copiesById = Object.fromEntries(selectedIds.map(id => [id, selected[id]?.copies || 1]));

  function toggleItem(item) {
    setSelected(prev => {
      const next = { ...prev };
      if (next[item.id]?.checked) {
        delete next[item.id];
      } else {
        next[item.id] = { checked: true, copies: 1, item };
      }
      return next;
    });
  }

  function setCopies(itemId, val) {
    const n = Math.min(99, Math.max(1, parseInt(val) || 1));
    setSelected(prev => ({ ...prev, [itemId]: { ...prev[itemId], copies: n } }));
  }

  function selectAll() {
    const next = {};
    filtered.forEach(item => { next[item.id] = { checked: true, copies: 1, item }; });
    setSelected(next);
  }

  function clearAll() { setSelected({}); }

  // Errors surface inside the modal, next to the control that caused them,
  // rather than on the page behind it. #335, Session 79.22b.6.3.2: this page
  // used to hand the finished run to a "reprint the last job" store nothing ever
  // read back; that store is deleted (Colby's R1 D1), so nothing is passed.

  // #199 part 3, Session 79.22b.6.1f (approved drawing C) -- the company has
  // turned QR label printing off. No button leads here any more, but an old link
  // or a bookmark still can (deepLink.js knows this page), so it SAYS so and who
  // can change it, rather than offering a print that should not happen. "Admin
  // or executive" because both may open Company settings (canAccessCompanySettings).
  // After every hook above.
  if (labelPrintingEnabled === false) {
    return (
      <div style={{ background: '#F5F5F3', minHeight: '100vh', padding: '24px 14px' }}>
        <div role="status" style={{ maxWidth: 480, margin: '0 auto', background: 'white', border: '1px solid #E8E8E8', borderRadius: 10, padding: '24px 16px', textAlign: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#0F0F0F', marginBottom: 6 }}>Label printing is turned off</div>
          <div style={{ fontSize: 13, color: '#5C5C5C', marginBottom: 14, lineHeight: 1.45 }}>
            {companyName} doesn&rsquo;t print QR labels in Stede. An admin or executive can turn it back on in Company settings.
          </div>
          <button onClick={() => setActive('inv_catalog')} className="btn btn-ghost btn-sm">Back to catalog</button>
        </div>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div style={{ background: '#F5F5F3', minHeight: '100vh', paddingBottom: 100 }}>
        <div style={{ background: '#fff', borderBottom: '1px solid #E8E8E8', position: 'sticky', top: 'var(--topbar-height)', zIndex: 10 }}>
          <div style={{ padding: '10px 14px 8px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={() => setActive('inv_catalog')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#1D9E75', display: 'flex', alignItems: 'center' }}>
              <IconChevronLeft size={20} />
            </button>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#0F0F0F' }}>Print labels</div>
              <div style={{ fontSize: 11, color: '#7A7A7A' }}>
                {selectedIds.length > 0 ? `${selectedIds.length} items · ${totalLabels} labels` : 'Select items to print'}
              </div>
            </div>
          </div>
          <div style={{ padding: '0 12px 8px', display: 'flex', gap: 6 }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, background: '#F0F0F0', borderRadius: 8, padding: '7px 10px' }}>
              <IconSearch size={13} color="#9A9A9A" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search items..." style={{ border: 'none', background: 'none', outline: 'none', color: '#0F0F0F', flex: 1 }} />
            </div>
            <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} style={{ height: 36, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 8px', color: '#5C5C5C', background: 'white' }}>
              <option value="">All</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {selectedIds.length > 0 && (
            <div style={{ padding: '0 12px 8px', display: 'flex', gap: 6 }}>
              <button onClick={clearAll} style={{ flex: 1, padding: '6px', borderRadius: 8, border: '1px solid #E0E0E0', background: 'white', fontSize: 11, color: '#7A7A7A', cursor: 'pointer' }}>Clear ({selectedIds.length})</button>
              <button onClick={selectAll} style={{ flex: 1, padding: '6px', borderRadius: 8, border: '1px solid #E0E0E0', background: 'white', fontSize: 11, color: '#7A7A7A', cursor: 'pointer' }}>Select all</button>
            </div>
          )}
        </div>

        <div style={{ padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {loading ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#9A9A9A', fontSize: 13 }}>Loading...</div>
          ) : filtered.length === 0 ? (
            <div style={{ background: 'white', borderRadius: 10, padding: '24px 16px', border: '1px solid #E8E8E8', textAlign: 'center' }}>
              <IconPackage size={28} color="#D0D0D0" />
              <div style={{ fontSize: 13, fontWeight: 600, color: '#9A9A9A', marginTop: 8 }}>No items found</div>
            </div>
          ) : filtered.map(item => {
            const stock = getStockForBranch(item, branchId);
            const isChecked = !!selected[item.id]?.checked;
            return (
              <div key={item.id} style={{ background: 'white', borderRadius: 10, border: `1px solid ${isChecked ? '#9FE1CB' : '#E8E8E8'}`, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <div
                  onClick={() => toggleItem(item)}
                  style={{ width: 20, height: 20, borderRadius: 4, border: `2px solid ${isChecked ? '#1D9E75' : '#D0D0D0'}`, background: isChecked ? '#1D9E75' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
                >
                  {isChecked && <IconX size={12} color="#fff" />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }} onClick={() => toggleItem(item)}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#0F0F0F', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</div>
                  <div style={{ fontSize: 10, color: '#7A7A7A' }}>{item.sku} · {item.uom} · {stock?.qty_on_hand ?? 0} on hand</div>
                </div>
                {isChecked && (
                  <input type="number" min={1} max={99} value={selected[item.id]?.copies || 1}
                    onChange={e => setCopies(item.id, e.target.value)}
                    onFocus={e => e.target.select()}
                    onClick={e => e.stopPropagation()}
                    style={{ width: 44, height: 36, border: '1px solid #E0E0E0', borderRadius: 8, textAlign: 'center', fontWeight: 700, color: '#0F0F0F', background: 'white' }} />
                )}
              </div>
            );
          })}
        </div>

        {selectedIds.length > 0 && (
          <div style={{ position: 'fixed', bottom: 70, left: 0, right: 0, padding: '10px 12px', background: 'white', borderTop: '1px solid #E8E8E8', zIndex: 50 }}>
            <button onClick={() => setShowPrintModal(true)}
              style={{ width: '100%', padding: 14, borderRadius: 12, border: 'none', background: '#1D9E75', color: 'white', fontSize: 14, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <IconPrinter size={18} />
              {`Print ${totalLabels} label${totalLabels !== 1 ? 's' : ''}`}
            </button>
          </div>
        )}
        <BatchPrintModal
          open={showPrintModal && selectedItems.length > 0}
          onClose={() => setShowPrintModal(false)}
          subjectType="inventory"
          items={selectedItems}
          initialCopies={copiesById}
          branchName={branchName}
          userId={profile?.id}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={() => setActive('inv_catalog')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#1D9E75', display: 'flex', alignItems: 'center' }}>
              <IconChevronLeft size={18} />
            </button>
            <div className="page-title">Print labels</div>
          </div>
          <div className="page-sub" style={{ marginLeft: 28 }}>{branchName} · select items and quantities, then choose a size and a printer</div>
        </div>
        {canSwitchBranch && (
          <select className="filter-select" value={branchId || ''} onChange={e => { setBranchId(parseInt(e.target.value)); setSelected({}); }}>
            {BRANCHES.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
      </div>

      <div className="toolbar" style={{ marginBottom: 12 }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 160 }}>
          <IconSearch size={14} color="#9A9A9A" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
          <input className="search-input" placeholder="Search items, SKU..." value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft: 32, width: '100%' }} />
        </div>
        <select className="filter-select" value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
          <option value="">All categories</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <button className="btn btn-ghost btn-sm" onClick={selectAll}>Select all ({filtered.length})</button>
        {selectedIds.length > 0 && (
          <button className="btn btn-ghost btn-sm" onClick={clearAll}>Clear ({selectedIds.length})</button>
        )}
      </div>

      {selectedIds.length > 0 && (
        <div style={{ background: '#e8f8f2', border: '0.5px solid #9FE1CB', borderRadius: 8, padding: '8px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ fontSize: 12, color: '#085041', fontWeight: 500 }}>
            {selectedIds.length} item{selectedIds.length !== 1 ? 's' : ''} selected · {totalLabels} label{totalLabels !== 1 ? 's' : ''}
          </div>
          <button onClick={() => setShowPrintModal(true)}
            style={{ background: '#1D9E75', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 18px', fontSize: 13, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            <IconPrinter size={14} />
            {`Print ${totalLabels} label${totalLabels !== 1 ? 's' : ''}`}
          </button>
        </div>
      )}

      <BatchPrintModal
        open={showPrintModal && selectedItems.length > 0}
        onClose={() => setShowPrintModal(false)}
        subjectType="inventory"
        items={selectedItems}
        initialCopies={copiesById}
        branchName={branchName}
        userId={profile?.id}
      />

      <div className="card">
        <div className="table-wrap">
          {loading ? (
            <div className="loading"><div className="spinner" />Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">🖨️</div>
              <div className="empty-state-title">No items found</div>
              <div className="empty-state-sub">Try adjusting your search or filters.</div>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th style={{ width: 32 }}>
                    <input type="checkbox" style={{ accentColor: '#1D9E75' }}
                      checked={filtered.length > 0 && filtered.every(item => selected[item.id]?.checked)}
                      onChange={e => e.target.checked ? selectAll() : clearAll()} />
                  </th>
                  <th>Item</th>
                  <th>SKU</th>
                  <th>Category</th>
                  <th>UOM</th>
                  <th>On hand</th>
                  <th style={{ width: 100 }}>Labels to print</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(item => {
                  const stock = getStockForBranch(item, branchId);
                  const isChecked = !!selected[item.id]?.checked;
                  return (
                    <tr key={item.id} style={{ background: isChecked ? '#f7fefb' : 'white', cursor: 'pointer' }} onClick={() => toggleItem(item)}>
                      <td onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={isChecked} onChange={() => toggleItem(item)} style={{ accentColor: '#1D9E75' }} />
                      </td>
                      <td style={{ fontWeight: isChecked ? 700 : 400 }}>{item.name}</td>
                      <td className="text-muted">{item.sku || '—'}</td>
                      <td className="text-muted">{item.inventory_categories?.name || '—'}</td>
                      <td className="text-muted">{item.uom}</td>
                      <td style={{ fontWeight: 500 }}>{stock?.qty_on_hand ?? 0}</td>
                      <td onClick={e => e.stopPropagation()}>
                        {isChecked ? (
                          <input type="number" min={1} max={99} value={selected[item.id]?.copies || 1}
                            onChange={e => setCopies(item.id, e.target.value)}
                            onFocus={e => e.target.select()}
                            style={{ width: 54, background: '#f7f7f7', border: '0.5px solid #ddd', borderRadius: 6, padding: '4px 6px', fontWeight: 700, textAlign: 'center', color: '#1a1a1a' }} />
                        ) : (
                          <span style={{ color: '#D0D0D0', fontSize: 12 }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}